import { hasPermissionCode, PERMISSIONS } from '@/lib/permissions';
import type { PermissionCatalogEntry } from '@/types';

export interface AccessRequirement {
  permissionCodes?: readonly string[];
  resource?: string;
  action?: string;
  includeScoped?: boolean;
}

function normalizeValue(value: string | undefined) {
  return value?.trim().toLowerCase() ?? '';
}

function dedupeCodes(codes: string[]) {
  const normalized = new Set<string>();
  for (const code of codes) {
    const trimmed = code.trim();
    if (trimmed.length > 0) normalized.add(trimmed);
  }
  return Array.from(normalized);
}

function buildFallbackCodes(requirement: AccessRequirement) {
  if (requirement.permissionCodes?.length) {
    return [...requirement.permissionCodes];
  }

  if (!requirement.resource || !requirement.action) {
    return [];
  }

  const baseCode = `${requirement.resource}.${requirement.action}`;
  if (!requirement.includeScoped) {
    return [baseCode];
  }

  return [baseCode, `${baseCode}.all`, `${baseCode}.own`];
}

function resolveCatalogCodes(requirement: AccessRequirement, catalog: PermissionCatalogEntry[]) {
  if (!requirement.resource || !requirement.action || catalog.length === 0) {
    return [];
  }

  const resource = normalizeValue(requirement.resource);
  const action = normalizeValue(requirement.action);

  const codes: string[] = [];
  for (const permission of catalog) {
    if (normalizeValue(permission.resource) !== resource) continue;

    const permissionAction = normalizeValue(permission.action);
    if (permissionAction === action || (requirement.includeScoped && permissionAction.startsWith(`${action}.`))) {
      codes.push(permission.code);
    }
  }
  return codes;
}

export function resolveAccessRequirementCodes(requirement: AccessRequirement, catalog: PermissionCatalogEntry[]) {
  return dedupeCodes([...resolveCatalogCodes(requirement, catalog), ...buildFallbackCodes(requirement)]);
}

export function hasAccessRequirement(
  userPermissionCodes: Iterable<string>,
  requirement: AccessRequirement,
  catalog: PermissionCatalogEntry[],
) {
  const codes = resolveAccessRequirementCodes(requirement, catalog);
  if (codes.length === 0) return false;

  return codes.some((code) => hasPermissionCode(userPermissionCodes, code));
}

export const ACCESS_REQUIREMENTS = {
  DASHBOARD: {
    permissionCodes: [
      PERMISSIONS.DASHBOARD_READ,
      PERMISSIONS.DASHBOARD_ADMIN_READ,
      PERMISSIONS.DASHBOARD_PROFESSIONAL_READ,
    ],
  },
  AGENDA_READ: {
    resource: 'booking',
    action: 'read',
    includeScoped: true,
  },
  BOOKING_CREATE: {
    resource: 'booking',
    action: 'create',
    includeScoped: true,
  },
  BOOKING_UPDATE: {
    resource: 'booking',
    action: 'update',
    includeScoped: true,
  },
  BOOKING_STATUS_UPDATE: {
    resource: 'booking',
    action: 'status.update',
    includeScoped: true,
  },
  BOOKING_AVAILABILITY_READ: {
    resource: 'booking',
    action: 'availability.read',
    includeScoped: true,
  },
  WAITING_LIST_READ: {
    resource: 'waiting-list',
    action: 'read',
  },
  WALK_IN_QUEUE_READ: {
    resource: 'walk-in-queue',
    action: 'read',
    includeScoped: true,
  },
  WALK_IN_QUEUE_CREATE: {
    resource: 'walk-in-queue',
    action: 'create',
    includeScoped: true,
  },
  WALK_IN_QUEUE_STATUS_UPDATE: {
    resource: 'walk-in-queue',
    action: 'status.update',
    includeScoped: true,
  },
  WALK_IN_ENTRY_CREATE: {
    resource: 'walk-in-queue.entry',
    action: 'create',
    includeScoped: true,
  },
  WALK_IN_ENTRY_STATUS_UPDATE: {
    resource: 'walk-in-queue.entry',
    action: 'status.update',
    includeScoped: true,
  },
  ATTENDANCE_READ: {
    resource: 'attendance',
    action: 'read',
    includeScoped: true,
  },
  ATTENDANCE_CREATE: {
    resource: 'attendance',
    action: 'create',
    includeScoped: true,
  },
  ATTENDANCE_UPDATE: {
    resource: 'attendance',
    action: 'update',
    includeScoped: true,
  },
  ATTENDANCE_STATUS_UPDATE: {
    resource: 'attendance',
    action: 'status.update',
    includeScoped: true,
  },
  SERVICES_READ: {
    permissionCodes: [PERMISSIONS.SERVICE_READ],
  },
  PRODUCTS_READ: {
    permissionCodes: [PERMISSIONS.PRODUCT_READ],
  },
  PROFESSIONALS_READ: {
    permissionCodes: [PERMISSIONS.USER_COMPANY_PROFESSIONAL_READ],
  },
  CUSTOMERS_READ: {
    permissionCodes: [PERMISSIONS.USER_COMPANY_CUSTOMER_READ],
  },
  WORKING_HOURS_READ: {
    permissionCodes: [PERMISSIONS.COMPANY_WORKING_HOUR_READ],
  },
  COMPANY_READ: {
    permissionCodes: [PERMISSIONS.COMPANY_READ],
  },
  RBAC_MANAGE: {
    permissionCodes: [PERMISSIONS.RBAC_MANAGE],
  },
} as const satisfies Record<string, AccessRequirement>;
